import { useState } from 'react';
import { WorkoutService } from '../../../../lib/workoutService';
import { displayNameFromProfile } from '../../../../pages/account/accountShared';
import { toLocalDateKey } from '../../../../utils/dateKey';
import type { AssignMembersCache } from './useAssignMembersCache';

export type AssignWorkoutSuccessToast = {
  workoutName: string;
  memberDisplayName: string;
};

export type UseAssignWorkoutModalParams = {
  workouts: Array<{ id: string; name?: string }>;
  setOpenWorkoutMenu: (id: string | null) => void;
  onNavigateToAddMember: () => void;
  assignMembersCache: AssignMembersCache;
};

/** Assign workout modal — Library workouts tab desktop (P15). */
export function useAssignWorkoutModal({
  workouts,
  setOpenWorkoutMenu,
  onNavigateToAddMember,
  assignMembersCache,
}: UseAssignWorkoutModalParams) {
  const { assignMembers, loadingAssignMembers, loadAssignMembersIfNeeded } = assignMembersCache;

  const [showAssignWorkoutModal, setShowAssignWorkoutModal] = useState(false);
  const [assigningWorkoutId, setAssigningWorkoutId] = useState<string | null>(null);
  const [selectedAssignWorkoutMemberId, setSelectedAssignWorkoutMemberId] = useState('');
  const [workoutAssignmentDate, setWorkoutAssignmentDate] = useState<string>(() => toLocalDateKey(new Date()));
  const [isAssigningWorkout, setIsAssigningWorkout] = useState(false);
  const [assignWorkoutError, setAssignWorkoutError] = useState<string | null>(null);
  const [assignWorkoutSuccessToast, setAssignWorkoutSuccessToast] = useState<AssignWorkoutSuccessToast | null>(
    null,
  );

  const handleAssignWorkout = async (workoutId: string) => {
    setOpenWorkoutMenu(null);
    setAssigningWorkoutId(workoutId);
    setSelectedAssignWorkoutMemberId('');
    setWorkoutAssignmentDate(toLocalDateKey(new Date()));
    setAssignWorkoutError(null);
    setShowAssignWorkoutModal(true);

    const loadError = await loadAssignMembersIfNeeded();
    if (loadError) setAssignWorkoutError(loadError);
  };

  const handleCloseAssignWorkoutModal = () => {
    setShowAssignWorkoutModal(false);
    setAssigningWorkoutId(null);
    setSelectedAssignWorkoutMemberId('');
    setWorkoutAssignmentDate(toLocalDateKey(new Date()));
    setAssignWorkoutError(null);
  };

  const handleAddMemberFromEmptyWorkout = () => {
    handleCloseAssignWorkoutModal();
    onNavigateToAddMember();
  };

  const handleAssignWorkoutSubmit = async () => {
    if (!selectedAssignWorkoutMemberId) {
      setAssignWorkoutError('Please select a member');
      return;
    }
    if (!workoutAssignmentDate) {
      setAssignWorkoutError('Please select a workout date');
      return;
    }
    if (!assigningWorkoutId) {
      setAssignWorkoutError('Workout ID is missing');
      return;
    }

    try {
      setIsAssigningWorkout(true);
      setAssignWorkoutError(null);

      await WorkoutService.assignWorkoutToMember(
        selectedAssignWorkoutMemberId,
        assigningWorkoutId,
        workoutAssignmentDate,
      );

      const workout = workouts.find((w) => w.id === assigningWorkoutId);
      const member = assignMembers.find((m) => m.memberId === selectedAssignWorkoutMemberId);
      const memberDisplayName =
        (member && (displayNameFromProfile(member.member) || member.member?.email)) || 'member';

      handleCloseAssignWorkoutModal();
      setAssignWorkoutSuccessToast({
        workoutName: workout?.name || 'Workout',
        memberDisplayName,
      });
    } catch (err: unknown) {
      console.error('Error assigning workout:', err);
      setAssignWorkoutError(
        err instanceof Error ? err.message : 'Failed to assign workout. Please try again.',
      );
    } finally {
      setIsAssigningWorkout(false);
    }
  };

  return {
    showAssignWorkoutModal,
    assigningWorkoutId,
    assignMembers,
    loadingAssignMembers,
    selectedAssignWorkoutMemberId,
    setSelectedAssignWorkoutMemberId,
    workoutAssignmentDate,
    setWorkoutAssignmentDate,
    assignWorkoutError,
    isAssigningWorkout,
    assignWorkoutSuccessToast,
    setAssignWorkoutSuccessToast,
    handleAssignWorkout,
    handleCloseAssignWorkoutModal,
    handleAssignWorkoutSubmit,
    handleAddMemberFromEmptyWorkout,
  };
}
